<template>
	<div class="fi-row">
		<span class="fi-rowIcon"><sym-icon :name="icon"></sym-icon></span>
		<div class="fi-row-message">
			<fi-message :fi="fi" :responsible="responsible" :entered-by="enteredBy" ref="messageSpan"></fi-message>
			<it-link v-if="needEmitLink" :to="fi.customURL" v-preview="linkToPreviewKey(fi.customURL)">{{ fi.customLinkName || fi.customURL }}</it-link>
			<object-link v-if="needEmitObject" :src-id="fi.srcId" :src-type="fi.srcType">{{ fi.srcName }}</object-link>
			<ui-root v-if="fi.taskForm" :data="fi.taskData" :part="fi.taskForm" :readonly="!isMe" :viewmode="false" :errs="errors" :display="{ mode: 'body' }"></ui-root>
		</div>
	</div>
</template>

<script setup lang="ts">
import {ref, computed, type Ref, provide, reactive, onUnmounted} from "vue";
import {ForceInfo} from "@t/processes/ForceInfo.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import FormPart from "@fd/containers/FormPart.vue";
import {ErrorMap, errorMapKey} from "@fd/PartInjectKeys.ts";
import {useForceInfoStore} from "@/store/ForceInfoStore";
import {ApiError, ApiErrorResponse} from "@t/util/ApiError.ts";
import ObjectLink from "@fd/helpers/ObjectLink.vue";
import FiMessage from "@/processes/FiMessage.vue";
import ItLink from "@/router/ItLink.vue";
import {SrcType} from "@t/meta/SourceType.ts";
import {linkToPreviewKey} from "@/utils/WebPreview";
import UiRoot from "@fd/containers/UiRoot.vue";
import {CommandHandlerResult} from "@fd/Types.ts";

const props = defineProps<{
	fi: ForceInfo,
	responsible: string,
	enteredBy: string
	/** Reload current page on force info completion (use on JSP pages) */
	reload?: boolean
}>();
const env = injectEnv();
const fis = useForceInfoStore();
const icon = computed<string>(() => {
	if (props.fi.icon) return props.fi.icon;
	switch (props.fi.srcType) {
		case SrcType.EVENT:
			return "event";
		case SrcType.CONTACT:
			return "communication";
		case SrcType.PROBLEM:
			return "flash";
		case SrcType.OPPORTUNITY:
			return "opportunity";
		case SrcType.BUSCASE:
			return "case";
		case SrcType.TASK:
		default: return "task";
	}
});
const messageSpan = ref(null);
const needEmitLink = computed<boolean>(() => messageSpan.value != null && messageSpan.value.hasUnusedMark('link'));
const needEmitObject = computed<boolean>(() => messageSpan.value != null && messageSpan.value.hasUnusedMark('object'));
const errors:ErrorMap = reactive<ErrorMap>([]);
const bus = connectEventBus("MicroTask");

const isMe = computed<boolean>(() => {
	return env.user && props.fi.responsible === env.user.login;
	//return false;
});

function processSubmitProblems(problems:ApiError[]):void {
	if (!Array.isArray(problems)) return;
	errors.length = 0;
	for (let p of problems) {
		errors.push(p);
	}
}

bus.onAnyCommand(async (cmd, data, localPart, ...extraArgs):Promise<CommandHandlerResult> => {
	errors.length = 0;	// clear errors
	const resp:ApiErrorResponse = await fis.executeForceInfoAction(props.fi.id, cmd, data);
	if (resp.ok) {
		if (!!props.reload) {
			// Reload current page
			window.location.reload();
		}
		return "ok";
	} else {
		processSubmitProblems(resp.problems);
		return "fail";
	}
});
</script>

<style>

</style>